<template>
  <new-edit-page
    :page-title="pageTitle"
    :submit-button-label="submitButtonLabel"
    :submit-button-disabled="submitButtonDisabled"
    :is-loading="loading"
    @submit="submit"
  >
    <template v-slot:leftSideContent>
      <v-layout row justify-space-between wrap>
        <div class="input-field-container medium">
          <label class="required" for="name">{{ $tc("Name", 1) }}</label>
          <input
            v-model="listValueData.name"
            id="name"
            class="input-field"
            :disabled="actionType === 'edit'"
          />
        </div>

        <div
          class="input-field-container medium"
          v-if="!listValueData.custom_list"
        >
          <label for="client_label">
            {{ $t("ClientLabel") }}
          </label>
          <input
            v-model="listValueData.client_label"
            id="client_label"
            class="input-field"
          />
        </div>

        <div class="input-field-container">
          <label for="definition">{{ $t("Definition") }}</label>
          <input
            v-model="listValueData.definition"
            id="definition"
            class="input-field"
          />
        </div>

        <div
          v-if="isSuperAdmin && !listValueData.custom_list"
          class="input-field-container medium radio-container"
        >
          <label for="deleted">
            {{ $t("Hidden") }}
          </label>
          <v-radio-group
            id="deleted"
            v-model="listValueData.hidden"
            class="radio-group"
            row
            mandatory
          >
            <v-radio
              :disabled="listValueData.custom_list"
              :label="$t('Yes')"
              :value="true"
              :ripple="false"
              color="accent"
            />
            <v-radio
              :disabled="listValueData.custom_list"
              :label="$t('No')"
              :value="false"
              :ripple="false"
              color="accent"
            />
          </v-radio-group>
        </div>
      </v-layout>
    </template>

    <template v-slot:rightSideHeader>
      <div style="display: flex; flex-direction: row">
        <management-tabs-and-search
          :tabs="tabs"
          :open-tab="openTab"
          @update-open-tab="openTab = $event"
        />
        <management-add-button
          :label="$t('AddItem')"
          @buttonClick="openListItemForm"
          :list-missing-permissions="[]"
        />
      </div>
    </template>

    <template v-slot:rightSideContent>
      <v-tabs-items v-model="openTab" :key="openTab">
        <v-tab-item value="listItems">
          <list-item-table
            :listUuid="listValueData.uuid"
            :isCustomList="listValueData.custom_list"
            :newListItems="listItems"
            @updateListItems="updateListItems"
          />
        </v-tab-item>
      </v-tabs-items>
      <list-item-popup-form
        :open="listItemFormIsOpen"
        @add="newListValue"
        @close="listItemFormIsOpen = false"
      >
      </list-item-popup-form>
    </template>
  </new-edit-page>
</template>

<script>
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import NewEditPage from "@/components/Management/NewEditPage/NewEditPage";
import ManagementTabsAndSearch from "@/components/_Organisms/ManagementTabsAndSearch";
import ListItemTable from "@/components/SystemManagement/SystemSettings/ListsSettings/ListItemTable";
import ListItemPopupForm from "@/components/SystemManagement/SystemSettings/ListsSettings/ListItemPopupForm";
import ManagementAddButton from "@/components/Management/ManagementAddButton";
import { mapActions } from "vuex";
import utils from "@/utils";

export default {
  name: "ListForm",

  components: {
    NewEditPage,
    ManagementTabsAndSearch,
    ListItemTable,
    ListItemPopupForm,
    ManagementAddButton
  },

  mixins: [checkPermissionsMixin],

  props: {
    /**
     * Data of the list
     */
    listValueData: {
      type: Object
    },

    loading: {
      type: Boolean
    },

    /**
     * Action performed by the user
     * Can be 'edit' or 'create'
     */
    actionType: {
      type: String,
      required: true
    },

    /**
     * Indicates if the submit button is disabled
     */
    submitButtonDisabled: {
      type: Boolean
    }
  },

  data() {
    return {
      openTab: "listItems",

      /**
       * Indicates if the form for the creation of a list item is open or not
       */
      listItemFormIsOpen: false,

      /**
       * List of items of the list
       */
      listItems: []
    };
  },

  computed: {
    /**
     * Returns the page title, according to the action being performed by the user
     * @return {String}
     */
    pageTitle() {
      return this.actionType === "create"
        ? this.$i18n.t("AddCustomList")
        : this.$i18n.t("UpdateList");
    },

    /**
     * Return the label of the submit button, according to the action being performed by the user
     * @return {String}
     */
    submitButtonLabel() {
      return this.actionType === "create"
        ? this.$i18n.t("Create")
        : this.$i18n.t("Save");
    },

    /**
     * Returns the tabs of the right side content section
     * @return {Array}
     */
    tabs() {
      return [
        {
          value: "listItems",
          text: this.$i18n.tc("Value", 2)
        }
      ];
    },

    /**
     * Gets the name of the list from the route parameters and returns it
     * @return {String}
     */
    listName() {
      return this.$route.params.listName;
    }
  },

  methods: {
    ...mapActions(["createCustomListValue", "CREATE_NEW_LIST_ITEM"]),

    /**
     * Emits an event, signaling that the list can be saved
     */
    submit() {
      this.$emit("save");
    },

    /**
     * Opens the form for the creation of a new list item
     */
    openListItemForm() {
      this.listItemFormIsOpen = true;
    },

    /**
     * Adds an item to the array of list items, closes the form and emits an event, with the updated list, to update the information
     * in the parent components
     */
    addItem(event) {
      this.listItems.push({
        code: event.code,
        libelle: event.libelle,
        client_code: event.client_code,
        client_label: event.client_label,
        definition: event.definition,
        index: event.index,
        blocked: event.blocked,
        deleted: event.deleted
      });
      this.listItemFormIsOpen = false;
      this.$emit("updateListItems", this.listItems);
    },

    /**
     * Creates a new list value.
     * If the list if custom the only data to send is the code and the libelle, along with the list ID.
     * Else, the whole object is sent.
     * The methods executed to create the items are different because the endpoints in the BE are different.
     */
    async newListValue($event) {
      if (this.$route.name === "custom-list-create") {
        this.addItem($event);
      } else {
        try {
          this.loading = true;
          let listName = this.listValueData.code.replace("l_", "list_");
          let isCustomList = this.listValueData.custom_list;

          if (isCustomList) {
            await this.createCustomListValue(this.getDataToSend($event));
          } else {
            await this.CREATE_NEW_LIST_ITEM({
              listName: listName,
              listValueData: this.getDataToSend($event)
            });
          }
          this.$toasted.global.toast_success("Item added to the list.");
        } catch (error) {
          let errorMessage = utils.errorHandler(error);
          if (typeof errorMessage === "string") {
            this.$toasted.global.toast_error(errorMessage);
          }
        } finally {
          this.listItemFormIsOpen = false;
          this.loading = false;
        }
      }
    },

    /**
     * Updates the list variable and emits an event to update the parent components' lists
     */
    updateListItems($event) {
      this.$emit("updateListItems", $event);
      this.listItems = $event;
    },

    getDataToSend(data) {
      return this.listValueData.custom_list
        ? {
            list_id: this.listValueData.id,
            code: data.code,
            libelle: data.libelle,
            index: data.index
          }
        : {
            blocked: data.blocked,
            client_code: data.client_code,
            client_label: data.client_label,
            code: data.code,
            definition: data.definition,
            deleted: data.deleted,
            index: data.index,
            libelle: data.libelle
          };
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SystemManagement/SystemSettings/ListsSettings/ListForm.vue